import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ForwardOutlinedIcon from "@mui/icons-material/ForwardOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard } from "../ui";
import { useAuth } from "../../context/AuthContext";
import MessageConversationView from "./MessageConversationView";

function timeAgo(date) {
  const d = new Date(date); const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000), hr = Math.floor(min / 60), day = Math.floor(hr / 24);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
function fmtFull(d) {
  return new Date(d).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
}
function initials(s) {
  if (!s) return "?";
  const p = String(s).trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
}
function receiverName(m) {
  return m.receiverName || m.receiver?.name || m.receiver || "Unknown";
}
function receiverEmail(m) {
  return m.receiverEmail || m.receiver?.email || "";
}
function receiverId(m) {
  const id = m.receiverId || m.receiver?._id || m.receiver?.id;
  return id ? String(id) : "";
}
function avatarColor(name) {
  const colors = ["#6C50BF", "#39C645", "#F9BC06", "#5C85FF", "#EC4899", "#F97316", "#14B8A6"];
  let s = 0; for (const c of String(name || "")) s += c.charCodeAt(0);
  return colors[s % colors.length];
}
function fileIcon(url = "") {
  const ext = url.split(".").pop().toLowerCase();
  if (ext === "pdf") return <PictureAsPdfOutlinedIcon sx={{ color: "#EF4444" }} />;
  return <InsertDriveFileOutlinedIcon sx={{ color: "primary.main" }} />;
}

export default function MySentMessagesNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/mailmessage/getAllMessagesasSender");
      const list = Array.isArray(res.data?.messages) ? res.data.messages
        : Array.isArray(res.data?.sent) ? res.data.sent
        : Array.isArray(res.data) ? res.data : [];
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setMessages(list);
      if (list.length && !activeId) setActiveId(list[0]._id);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load sent messages");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Group by subject to show "threads" (simple grouping: same subject + same receiver counted once)
  const filtered = useMemo(() => {
    if (!search) return messages;
    const q = search.toLowerCase();
    return messages.filter((m) =>
      (m.subject || "").toLowerCase().includes(q) ||
      (m.message || "").toLowerCase().includes(q) ||
      receiverName(m).toLowerCase().includes(q)
    );
  }, [messages, search]);

  const active = messages.find((m) => m._id === activeId);

  // Stats
  const readCount = messages.filter((m) => m.read).length;
  const deliveredCount = messages.length;
  const uniqueRecipients = new Set(messages.map((m) => receiverEmail(m) || receiverName(m))).size;

  return (
    <Box p={3}>
      <PageHeader
        title="Sent"
        subtitle={`${messages.length} messages sent to ${uniqueRecipients} ${uniqueRecipients === 1 ? "person" : "people"}`}
        icon={<SendOutlinedIcon />}
        actions={[
          <Button key="inbox" variant="outlined" startIcon={<InboxOutlinedIcon />} onClick={() => nav("/MyMessages")}>
            Inbox
          </Button>,
          <Button key="compose" variant="contained" startIcon={<MailOutlineOutlinedIcon />} onClick={() => nav("/SendMessage")}>
            New Message
          </Button>,
        ]}
      />

      {messages.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
          <MiniStat label="Sent" value={deliveredCount} icon={<SendOutlinedIcon />} accent="#6C50BF" />
          <MiniStat label="Read" value={readCount} icon={<CheckCircleOutlineIcon />} accent="#39C645" />
          <MiniStat label="Unread" value={deliveredCount - readCount} icon={<AccessTimeOutlinedIcon />} accent="#F9BC06" />
          <MiniStat label="Recipients" value={uniqueRecipients} icon={<MailOutlineOutlinedIcon />} accent="#5C85FF" />
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "380px 1fr" }, gap: 2, minHeight: 560 }}>
        {/* LIST */}
        <SectionCard sx={{ p: 0, overflow: "hidden", display: { xs: active ? "none" : "block", md: "block" } }}>
          <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 1 }}>
            <TextField
              fullWidth size="small" placeholder="Search…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <IconButton size="small" onClick={load} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: { md: "calc(100vh - 300px)" }, overflowY: "auto" }}>
            {loading && messages.length === 0 ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress size={24} /></Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                <SendOutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                <Typography sx={{ fontSize: 13 }}>
                  {search ? "No matches." : "You haven't sent any messages yet."}
                </Typography>
                {!search && (
                  <Button startIcon={<MailOutlineOutlinedIcon />} onClick={() => nav("/SendMessage")} sx={{ mt: 2 }}>
                    Compose your first message
                  </Button>
                )}
              </Box>
            ) : (
              filtered.map((m) => (
                <SentRow key={m._id} message={m} active={activeId === m._id} onClick={() => setActiveId(m._id)} />
              ))
            )}
          </Box>
        </SectionCard>

        {/* PREVIEW */}
        <SectionCard sx={{ p: 0, overflow: "hidden", display: { xs: active ? "block" : "none", md: "block" } }}>
          {!active ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 400, color: "text.secondary", p: 4 }}>
              <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: "#F4EDFA", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                <SendOutlinedIcon sx={{ fontSize: 36, color: "primary.main" }} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>No selection</Typography>
              <Typography sx={{ fontSize: 13, textAlign: "center", maxWidth: 300 }}>
                Pick a sent message on the left to see its full content and read receipt.
              </Typography>
            </Box>
          ) : (
            <SentDetail
              message={active}
              myUserId={user?._id != null ? String(user._id) : user?.id != null ? String(user.id) : ""}
              onBack={() => setActiveId(null)}
              onSent={load}
              onForward={() => nav(`/SendMessage?subject=${encodeURIComponent("Fwd: " + (active.subject || ""))}&body=${encodeURIComponent("\n\n---- Forwarded ----\n" + (active.message || ""))}`)}
            />
          )}
        </SectionCard>
      </Box>
    </Box>
  );
}

function MiniStat({ label, value, icon, accent }) {
  return (
    <Box sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: "background.paper", borderBottom: `3px solid ${accent}` }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Box sx={{ color: accent }}>{icon}</Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: accent }}>{value}</Typography>
    </Box>
  );
}

function SentRow({ message, active, onClick }) {
  const name = receiverName(message);
  const color = avatarColor(name);
  const hasFiles = (message.files?.length || 0) > 0;
  const preview = String(message.message || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5, cursor: "pointer",
        borderLeft: "3px solid",
        borderLeftColor: active ? "primary.main" : "transparent",
        bgcolor: active ? "#F4EDFA" : "transparent",
        borderBottom: "1px solid", borderBottomColor: "divider",
        transition: "all 0.12s",
        "&:hover": { bgcolor: active ? "#F4EDFA" : "action.hover" },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ width: 36, height: 36, bgcolor: color, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
          {initials(name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Typography component="span" sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                To
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {name}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 10.5, color: "text.secondary", whiteSpace: "nowrap", ml: 1, flexShrink: 0 }}>
              {timeAgo(message.createdAt)}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: 0.25 }}>
            {message.subject || "(no subject)"}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
            <Typography sx={{ fontSize: 11, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
              {preview || "(no body)"}
            </Typography>
            {hasFiles && <AttachFileOutlinedIcon sx={{ fontSize: 13, color: "text.secondary", flexShrink: 0 }} />}
            {message.read ? (
              <Tooltip title="Read">
                <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#39C645", flexShrink: 0 }} />
              </Tooltip>
            ) : (
              <Tooltip title="Delivered, not read yet">
                <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: "#F9BC06", flexShrink: 0 }} />
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function SentDetail({ message, myUserId, onBack, onSent, onForward }) {
  const name = receiverName(message);
  const email = receiverEmail(message);
  const color = avatarColor(name);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton onClick={onBack} sx={{ display: { md: "none" } }}><ArrowBackIcon /></IconButton>
        <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1, lineHeight: 1.35 }}>
          {message.subject || "(no subject)"}
        </Typography>
        <Chip
          label={message.read ? "Read" : "Unread"}
          size="small"
          icon={message.read ? <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} /> : <AccessTimeOutlinedIcon sx={{ fontSize: "14px !important" }} />}
          sx={{
            fontWeight: 700,
            bgcolor: message.read ? "#EDFAF2" : "#FEF3C7",
            color: message.read ? "#065F46" : "#92400E",
            "& .MuiChip-icon": { color: "inherit" },
          }}
        />
      </Box>
      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ width: 44, height: 44, bgcolor: color, fontSize: 14, fontWeight: 700 }}>{initials(name)}</Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Sent to</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{name}</Typography>
            {email && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{email}</Typography>}
          </Box>
          <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "right" }}>{fmtFull(message.createdAt)}</Typography>
        </Stack>
      </Box>

      <MessageConversationView
        conversationId={message.conversationId ? String(message.conversationId) : ""}
        myUserId={myUserId}
        otherName={name}
        otherEmail={email}
        subject={message.subject}
        seedMessage={message}
        onSent={onSent}
        footerExtra={
          <Button variant="outlined" startIcon={<ForwardOutlinedIcon />} onClick={onForward}>
            Forward
          </Button>
        }
      />
    </Box>
  );
}
